import { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { config } from '../config';

interface DailyAvailability {
  date: string;
  availability_percentage: string | number;
  incidents_count: string | number;
  maintenances_count: string | number;
}

interface AvailabilityGraphProps {
  days?: number;
}

export default function AvailabilityGraph({ days = 90 }: AvailabilityGraphProps) {
  const [data, setData] = useState<DailyAvailability[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = config.apiUrl;

  useEffect(() => {
    fetchAvailabilityData();
  }, [days]);

  async function fetchAvailabilityData() {
    try {
      setLoading(true);

      const summaryRes = await fetch(`${apiUrl}/v1/stats/availability/summary?days=${days}`);
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }

      const statsRes = await fetch(`${apiUrl}/v1/stats/availability/daily-aggregate?days=${days}`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setData(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch availability data', error);
    } finally {
      setLoading(false);
    }
  }

  function getAvailabilityColor(percentage: number): string {
    if (percentage >= 99.9) return 'bg-emerald-500';
    if (percentage >= 99.0) return 'bg-yellow-500';
    if (percentage >= 95.0) return 'bg-orange-500';
    return 'bg-rose-300';
  }

  function getBarHeight(percentage: number): number {
    return Math.max(2, (percentage / 100) * 100);
  }

  function toNumber(value: string | number | undefined): number {
    if (value === undefined || value === null) return 0;
    return typeof value === 'string' ? parseFloat(value) : value;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-8">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-100 rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-gray-50 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <h2 className="text-base font-bold text-navy-900">System Availability</h2>
            </div>
            <p className="text-xs text-gray-400 mt-1">Last {days} days</p>
          </div>
          {summary && (
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-600 tabular-nums">
                {toNumber(summary.avg_availability).toFixed(2)}%
              </div>
              <div className="text-[11px] text-gray-400 mt-0.5">
                Average availability
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Graph */}
        <div className="relative">
          <div className="flex items-end justify-between gap-[2px] h-28 mb-2">
            {data.slice(-90).map((day) => {
              const percentage = toNumber(day.availability_percentage);
              const height = getBarHeight(percentage);
              const color = getAvailabilityColor(percentage);
              const hasIncident = toNumber(day.incidents_count) > 0;
              const hasMaintenance = toNumber(day.maintenances_count) > 0;

              return (
                <div
                  key={day.date}
                  className="flex-1 flex flex-col justify-end group relative"
                  style={{ minWidth: '2px' }}
                >
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-navy-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-elevated">
                      <div className="font-semibold">
                        {new Date(day.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="mt-1 text-gray-300">
                        {toNumber(day.availability_percentage).toFixed(2)}% uptime
                      </div>
                      {hasIncident && (
                        <div className="text-rose-200 mt-0.5">
                          {day.incidents_count} incident{toNumber(day.incidents_count) > 1 ? 's' : ''}
                        </div>
                      )}
                      {hasMaintenance && (
                        <div className="text-blue-300 mt-0.5">
                          {day.maintenances_count} maintenance{toNumber(day.maintenances_count) > 1 ? 's' : ''}
                        </div>
                      )}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="border-4 border-transparent border-t-navy-900"></div>
                      </div>
                    </div>
                  </div>

                  {/* Bar */}
                  <div
                    className={`${color} rounded-sm transition-all duration-150 hover:opacity-75 ${
                      hasIncident ? 'ring-1 ring-rose-200 ring-offset-1' : ''
                    }`}
                    style={{ height: `${height}px` }}
                  ></div>
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex justify-between text-[11px] text-gray-400 mt-1">
            <span>
              {data.length > 0 && new Date(data[0].date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
            </span>
            <span>Today</span>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-emerald-500 rounded-sm"></div>
              <span className="text-xs text-gray-500">&ge; 99.9%</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-sm"></div>
              <span className="text-xs text-gray-500">99.0&ndash;99.9%</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 rounded-sm"></div>
              <span className="text-xs text-gray-500">95.0&ndash;99.0%</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-rose-300 rounded-sm"></div>
              <span className="text-xs text-gray-500">&lt; 95.0%</span>
            </div>
          </div>

          {summary && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-unlimit-gray-light rounded-xl px-4 py-3">
                <div className="text-lg font-bold text-navy-900 tabular-nums">
                  {summary.total_incidents || 0}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">Incidents</div>
              </div>
              <div className="bg-unlimit-gray-light rounded-xl px-4 py-3">
                <div className="text-lg font-bold text-navy-900 tabular-nums">
                  {summary.total_maintenances || 0}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">Maintenances</div>
              </div>
              <div className="bg-unlimit-gray-light rounded-xl px-4 py-3">
                <div className="text-lg font-bold text-navy-900 tabular-nums">
                  {summary.operational_components || 0}/{summary.total_components || 0}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">Operational</div>
              </div>
              <div className="bg-unlimit-gray-light rounded-xl px-4 py-3">
                <div className="text-lg font-bold text-emerald-600 tabular-nums">
                  {toNumber(summary.avg_uptime).toFixed(2)}%
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">Avg Uptime</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
